import React from 'react';
import { ShowIf } from '../../components/ShowIf';
import { CertificateOfOccupancyType, LoanType } from '@apply/constants';
import { useBorrowerLoanType } from '../../../queries/BorrowerFields/CoreFields';
import { usePropertyGeneralContractorWillWork } from '../../../queries/PropertyFields/NonRentalFlagFields';
import {
    usePropertyRentalMoreThan10kExpenditures,
    usePropertyRentalPurchasedLessThan2YearsAgo,
} from '../../../queries/PropertyFields/RentalFlagFields';
import {
    usePropertyBudgetFile,
    usePropertyPlansFile,
    usePropertySpecSheetFile,
    usePropertySurveyFile,
    usePropertyPresaleContractFile,
    usePropertyGcResumeFile,
    usePropertyProofOfOccupancyFile,
} from '../../../queries/PropertyFileFields/NonRentalFiles';
import { usePropertyPurchaseContractFile, usePropertyCapexFile } from '../../../queries/PropertyFileFields/RentalFiles';
import { usePropertyCertificateOfOccupancy } from '../../../queries/PropertyFields';

export type ProvideLaterAdvisoryProps = {
    propertyGuid: string;
    isReview: boolean;
};

export const ProvideLaterAdvisory: React.FC<ProvideLaterAdvisoryProps> = ({ propertyGuid, isReview = false }) => {
    const [loanType] = useBorrowerLoanType();

    const { handleMode: nonRentalBudgetHandle } = usePropertyBudgetFile(propertyGuid);
    const { handleMode: nonRentalPlansHandle } = usePropertyPlansFile(propertyGuid);
    const { handleMode: nonRentalSpecSheetHandle } = usePropertySpecSheetFile(propertyGuid);
    const { handleMode: nonRentalSurveyHandle } = usePropertySurveyFile(propertyGuid);
    const { handleMode: nonRentalPresaleContractHandle } = usePropertyPresaleContractFile(propertyGuid);
    const { handleMode: nonRentalGeneralContractorResumeHandle } = usePropertyGcResumeFile(propertyGuid);
    const [nonRentalGeneralContractorWillWork] = usePropertyGeneralContractorWillWork(propertyGuid);

    const { handleMode: rentalPurchaseContractHandle } = usePropertyPurchaseContractFile(propertyGuid);
    const [rentalPurchasedLessThan2YearsAgo] = usePropertyRentalPurchasedLessThan2YearsAgo(propertyGuid);
    const { handleMode: rentalCapitalExpendituresHandle } = usePropertyCapexFile(propertyGuid);
    const [rentalMoreThan10kExpenditures] = usePropertyRentalMoreThan10kExpenditures(propertyGuid);

    const { handleMode: proofOfOccupancyHandle } = usePropertyProofOfOccupancyFile(propertyGuid);
    const [certificateOfOccupancy] = usePropertyCertificateOfOccupancy(propertyGuid);

    const constructReviewFormsAdvisoryMessagePrefix = (): string | undefined => {
        if (!loanType) return undefined;

        const formsRequired = [];
        if ([LoanType.Construction, LoanType.BuildRent, LoanType.Model].includes(loanType)) {
            if (nonRentalBudgetHandle === 'providelater') {
                formsRequired.push('Budget');
            }
            if (nonRentalPlansHandle === 'providelater') {
                formsRequired.push('Plans');
            }
            if (nonRentalSpecSheetHandle === 'providelater') {
                formsRequired.push('Spec Sheet');
            }
            if (nonRentalSurveyHandle === 'providelater') {
                formsRequired.push('Survey');
            }
            if (nonRentalPresaleContractHandle === 'providelater') {
                formsRequired.push('Presale Contract');
            }
            if (nonRentalGeneralContractorResumeHandle === 'providelater' && nonRentalGeneralContractorWillWork == true) {
                formsRequired.push('G.C. Resume');
            }
        }

        if ([LoanType.Rental, LoanType.BuildRent].includes(loanType)) {
            if (rentalPurchaseContractHandle === 'providelater' && rentalPurchasedLessThan2YearsAgo === true) {
                formsRequired.push('Purchase Contract');
            }
            if (rentalCapitalExpendituresHandle === 'providelater' && rentalMoreThan10kExpenditures === true) {
                formsRequired.push('Capital Expenditures');
            }
        }

        const certificateRequired = [
            CertificateOfOccupancyType.Yes,
            CertificateOfOccupancyType.LocalEquivalent,
            CertificateOfOccupancyType.TCO,
        ];
        if (loanType == LoanType.Bridge) {
            if (
                proofOfOccupancyHandle === 'providelater' &&
                certificateOfOccupancy != undefined &&
                certificateRequired.includes(certificateOfOccupancy)
            ) {
                formsRequired.push('Proof of Occupancy');
            }
            if (nonRentalSurveyHandle === 'providelater') {
                formsRequired.push('Survey');
            }
        }

        if (formsRequired.length === 1) {
            return formsRequired[0];
        } else if (formsRequired.length === 2) {
            return formsRequired.join(' and ');
        } else if (formsRequired.length > 2) {
            const last = formsRequired.pop();
            return formsRequired.join(', ') + ' and ' + last;
        }

        return undefined;
    };

    return (
        <ShowIf showIf={isReview && !!constructReviewFormsAdvisoryMessagePrefix()}>
            <div
                style={{
                    padding: '15px 50px 15px 50px',
                }}
            >
                <strong>
                    <em>NOTE:</em>
                </strong>
                &nbsp; {constructReviewFormsAdvisoryMessagePrefix()} are/is needed to process your application and order a
                valuation. Please submit ASAP via&nbsp;
                <a href='mailto:team@builderfinance.com'>team@builderfinance.com</a>
                &nbsp; or&nbsp;
                <a
                    href='https://fileshare.bfiweb.com'
                    target='_blank'
                    rel='noreferrer'
                >
                    https://fileshare.bfiweb.com
                </a>
            </div>
        </ShowIf>
    );
};
